import { connect } from "react-redux";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { compose } from "redux";
import AwesomeIcon from "@icons/AwesomeIcon";
import { RootState } from "@/types/RootState";
import { isMenuRootEntry } from "@/types/NavigationState";
import { goBackOrTo } from "@/hooks/useGoBackOrTo";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";
import "./backhistory.css";

interface BackHistoryProps extends RouteComponentProps {
  global: {
    historyCount: number;
    isMobile: boolean;
    showAiChat?: boolean;
    showNotification?: boolean;
    menuType?: "MAIN" | "ADMIN";
    dashboardNotification?: unknown;
    preventSidebar?: boolean;
    newNotificationsCount?: number;
    reloadSidebar?: boolean;
    logoPath?: string;
    showCanvasMenu?: boolean;
  };
  /**
   * Canonical parent route (usually the entity's list page). Only used
   * when there is no in-app history — e.g. the detail page was opened
   * via a direct URL or bookmark — so the arrow can still offer a way
   * "up". With in-app history, goBack() always wins, returning to the
   * actual originating page (STIC-15588).
   */
  backTo?: string;
}

const BackHistory = ({ global, history, location, backTo }: BackHistoryProps) => {

  const backClicked = (ev?: React.MouseEvent<HTMLAnchorElement>) => {
    ev?.preventDefault();
    // historyCount is kept in sync by AppLayout's history listener;
    // the resulting POP updates it — no manual decrement needed. Shared with
    // save/cancel handlers via goBackOrTo so both behave identically. When
    // there is no history and no backTo, this is a no-op (the arrow is hidden
    // anyway — see the render guard below).
    if (global.historyCount > 0 || backTo) {
      goBackOrTo(history, global.historyCount, backTo ?? "");
    }
  };

  // Pages reached via a sidebar-menu click are top-level entries: no
  // back arrow there, even though in-app history exists. The state
  // lives on the history entry, so returning here via back keeps the
  // arrow hidden consistently (STIC-15588).
  if (isMenuRootEntry(location)) return null;

  if (global.historyCount <= 0 && !backTo) return null;

  return (
    <span className="leftBreakcrumb">
      <a
        href="# "
        onClick={(ev) => backClicked(ev)}
        title={translate(LanguageKey.back_overview_key)}
        className="me-2"
      >
        <AwesomeIcon
          className="back-history"
          icon="arrow-left"
          size="xl"
        ></AwesomeIcon>
      </a>
    </span>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    global: state.all,
  };
};

export default compose<React.ComponentType<{ backTo?: string }>>(
  withRouter,
  connect(mapStateToProps)
)(BackHistory) as React.ComponentType<{ backTo?: string }>;
